import ky from 'ky';

// ============ 类型定义 ============

/** Fetch 实例配置 */
export interface FetchConfig {
    baseUrl: string;
    headers?: Record<string, string>;
}

/** 单次请求选项 */
export interface RequestOptions {
    signal?: AbortSignal;
}

/** POST 请求体类型 (可 JSON 序列化的对象或数组) */
export type RequestBody = Record<string, unknown> | unknown[];

// ============ 工具函数 ============

/** 规范化路径: ky 的 prefixUrl 要求 path 不能以 / 开头 */
const normalizePath = (baseUrl: string, path: string) =>
    baseUrl && path.startsWith('/') ? path.slice(1) : path;

// ============ 工厂函数 ============

/**
 * ky 默认配置:
 * - timeout: 10000ms
 * - retry.limit: 2 (共 3 次请求)
 * - retry.statusCodes: 408/413/429/500/502/503/504
 * - retry.delay: 指数退避 (300ms, 600ms, ...)
 *
 * 自定义配置:
 * - retry.methods: 所有方法都重试 (ky 默认不含 POST)
 * - retry.retryOnTimeout: 超时后也重试 (ky 默认不重试)
 */
export const createFetch = (config: FetchConfig) => {
    const { baseUrl, headers } = config;

    const instance = ky.create({
        prefixUrl: baseUrl,
        headers,
        retry: {
            methods: ['get', 'post', 'put', 'head', 'delete', 'options', 'trace'],
            retryOnTimeout: true,
        },
    });

    return {
        get: <T>(path: string, options?: RequestOptions) =>
            instance.get(normalizePath(baseUrl, path), { signal: options?.signal }).json<T>(),

        post: <T>(path: string, body?: RequestBody, options?: RequestOptions) =>
            instance.post(normalizePath(baseUrl, path), { json: body, signal: options?.signal }).json<T>(),
    };
};
